// §12 opt-in recovery backstop — the printed recovery code.
//
// The code is 20 random bytes rendered as 32 Crockford-base32 characters
// (160 bits of entropy, displayed as 8 groups of 4). The NORMALIZED code
// string is fed to the same Argon2id wrap as the account password
// (shared/kek.js), producing a second entry in the identity bundle's `wraps`
// object: `wraps.recovery`, identical in shape to `wraps.password`.
//
// Lifecycle rules (enforced by the flows, not by this module):
//   - password change keeps the code valid (the wrapped secrets don't change);
//   - identity ROTATION deletes `wraps.recovery` — a rotation is a compromise
//     response, and the printed code is a total-access credential, so it is
//     deliberately voided; the user prints a fresh one via recovery-setup.

// Crockford base32: no I, L, O, U — ambiguous glyphs map back on input.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const CODE_LEN = 32; // 20 bytes * 8 / 5 bits

/** Generate a fresh code (normalized form, 32 chars). Print via recoveryCodeDisplay. */
export function generateRecoveryCode(sodium) {
  const bytes = sodium.randombytes_buf(20);
  let bits = 0, value = 0, out = '';
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  return out;
}

/**
 * Normalize user input to the canonical 32-char form: strip dashes/spaces,
 * uppercase, map the Crockford ambiguity classes (O→0, I/L→1). Throws if the
 * result is not a plausible code — better than deriving a KEK from a typo.
 */
export function normalizeRecoveryCode(str) {
  const cleaned = String(str).toUpperCase().replace(/[-\s]/g, '')
    .replace(/O/g, '0').replace(/[IL]/g, '1');
  if (cleaned.length !== CODE_LEN || [...cleaned].some((c) => !ALPHABET.includes(c))) {
    throw new Error(`Recovery code must be ${CODE_LEN} base32 characters (8 groups of 4).`);
  }
  return cleaned;
}

/** XXXX-XXXX-… grouping for printing/display. */
export function recoveryCodeDisplay(code) {
  return code.match(/.{4}/g).join('-');
}
